import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context';
import { Input, Card } from '../../components/common';
import { Link, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import AppButton from '../../components/common/AppButton';
import { paths } from '../../routing/paths';
import Button from '@mui/material/Button';
import AuthLayout from '../../components/layout/AuthLayout';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { appTermsUrl } from '../../constants';
import { privacyPolicyUrl } from '../../constants';

const Signup: React.FC = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (agreeToTerms) {
      setError('');
    }
  }, [agreeToTerms]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError('');

    if (!agreeToTerms) {
      setError('Please agree to the privacy policy and terms of service.');
      setLoading(false);
      return;
    }

    try {
      await login(email);
      setEmailSent(true);
    } catch (err) {
      setError('Failed to login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          p: 4,
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyItems: 'center',
        }}
        elevation={0}
      >
        <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 1 }}>
          Welcome to{' '}
          <Box component={'span'} sx={(theme) => ({ color: theme.palette.primary.main })}>
            ECGO
          </Box>
        </Typography>
        <Typography
          sx={(theme) => ({ mb: 2, textAlign: 'center', color: theme.palette.grey[500] })}
        >
          Create an account to get started!
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {emailSent ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            A confirmation link has been sent to your email address. Please check your inbox.
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <Input
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography sx={{ fontSize: 14 }}>
                    I agree to the{' '}
                    <Box
                      component={Link}
                      to={privacyPolicyUrl}
                      target="_blank"
                      sx={(theme) => ({ color: theme.palette.primary.main })}
                    >
                      Privacy Policy
                    </Box>{' '}
                    and{' '}
                    <Box
                      component={Link}
                      to={appTermsUrl}
                      target="_blank"
                      sx={(theme) => ({ color: theme.palette.primary.main })}
                    >
                      App Terms
                    </Box>
                  </Typography>
                }
                sx={(theme) => ({
                  mb: 2,
                  justifyContent: 'center',
                  width: '100%',
                  color: theme.palette.grey[600],
                })}
              />
            </Box>
            <AppButton type="submit" isFullWidth isLoading={loading} style={{ marginTop: 16 }}>
              Create account
            </AppButton>
          </form>
        )}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2">
            Already have an account?
            <Link to={paths.login} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Button
                variant="text"
                sx={{ textTransform: 'none', textDecoration: 'underline', ml: 0.5 }}
              >
                Sign in
              </Button>
            </Link>
          </Typography>
        </Box>
      </Card>
    </AuthLayout>
  );
};

export default Signup;
